/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Search,
  Download,
  FileText,
  Calendar,
  Layers,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { docsApi, formatFileSize, formatDate } from "./documents_module/api";
import "./styles/documents.css";

const PAGE_SIZE = 12;

const Documents = ({ heading = "Downloads" }) => {
  const [cats, setCats] = useState([]);
  const [total, setTotal] = useState(0); // grand total across all categories
  const [selCat, setSelCat] = useState(null); // category key | null (All)

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // debounced
  const [page, setPage] = useState(0);

  const [docs, setDocs] = useState([]);
  const [resultTotal, setResultTotal] = useState(0); // total for current filter
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Categories for the sidebar (only those that hold public docs).
  useEffect(() => {
    let cancelled = false;
    docsApi
      .publicCategories()
      .then((res) => {
        if (cancelled) return;
        setCats(res?.categories || []);
        setTotal(res?.total || 0);
      })
      .catch(() => {
        /* sidebar is best-effort; the list still works */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to the first page whenever the filter changes.
  useEffect(() => {
    setPage(0);
  }, [selCat, search]);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    docsApi
      .publicList({
        category: selCat || undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      .then((res) => {
        if (cancelled) return;
        setDocs(res?.documents || []);
        setResultTotal(res?.total || 0);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Could not load downloads");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selCat, search, page]);

  useEffect(() => load(), [load]);

  const pageCount = Math.max(1, Math.ceil(resultTotal / PAGE_SIZE));
  const rangeStart = resultTotal === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(resultTotal, (page + 1) * PAGE_SIZE);

  return (
    <div className="pubdocs">
      <div className="pubdocs-head">
        <h2>{heading}</h2>
        <p>Documents the society has made available for the public to download.</p>
      </div>

      <div className="pubdocs-body">
        {/* Category sidebar */}
        <aside className="pubdocs-side">
          <div className="pubdocs-side-h">Categories</div>
          <button
            className={`pubdocs-cat ${!selCat ? "active" : ""}`}
            onClick={() => setSelCat(null)}
          >
            <span className="pubdocs-cat-ic">
              <Layers size={15} />
            </span>
            <span className="pubdocs-cat-name">All documents</span>
            <span className="pubdocs-cat-n">{total}</span>
          </button>
          {cats.map((c) => (
            <button
              key={c.key}
              className={`pubdocs-cat ${selCat === c.key ? "active" : ""}`}
              onClick={() => setSelCat(selCat === c.key ? null : c.key)}
            >
              <span
                className="pubdocs-cat-dot"
                style={{ background: c.color || "#94a3b8" }}
              />
              <span className="pubdocs-cat-name">{c.label}</span>
              <span className="pubdocs-cat-n">{c.count}</span>
            </button>
          ))}
        </aside>

        {/* Results */}
        <div className="pubdocs-main">
          <div className="pubdocs-search">
            <Search size={16} />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search documents…"
            />
          </div>

          {error && <p className="pubdocs-error">{error}</p>}

          {!error && (
            <div className="pubdocs-meta">
              {loading
                ? "Loading…"
                : resultTotal === 0
                ? "No documents found"
                : `Showing ${rangeStart}–${rangeEnd} of ${resultTotal}`}
            </div>
          )}

          {!loading && !error && resultTotal === 0 && (
            <div className="pubdocs-empty">
              <FileText size={30} strokeWidth={1.4} />
              <p>
                {search || selCat
                  ? "Nothing matches your filter. Try a different search or category."
                  : "No public documents available right now. Check back soon."}
              </p>
            </div>
          )}

          {docs.length > 0 && (
            <div className="pubdocs-list">
              {docs.map((doc) => (
                <div key={doc.id} className="pubdocs-row">
                  <span className="pubdocs-card-ic">
                    <FileText size={18} />
                  </span>
                  <div className="pubdocs-row-body">
                    <div className="pubdocs-row-head">
                      <h3 className="pubdocs-card-title" title={doc.title}>
                        {doc.title}
                      </h3>
                      {doc.category && (
                        <span className="pubdocs-card-cat">{doc.category}</span>
                      )}
                    </div>
                    {doc.description && (
                      <p className="pubdocs-card-desc">{doc.description}</p>
                    )}
                    <div className="pubdocs-card-meta">
                      <span>
                        <Calendar size={12} /> {formatDate(doc.uploaded_at)}
                      </span>
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>
                        <Download size={12} />{" "}
                        {(doc.download_count || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                  <a
                    className="pubdocs-card-btn"
                    href={docsApi.publicDownloadUrl(doc.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <Download size={14} /> Download
                  </a>
                </div>
              ))}
            </div>
          )}

          {resultTotal > PAGE_SIZE && (
            <div className="pubdocs-pager">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft size={15} /> Prev
              </button>
              <span>
                Page {page + 1} of {pageCount}
              </span>
              <button
                disabled={page + 1 >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next <ChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents;
